import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

type AppRole = 'admin' | 'moderator' | 'user';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  rolesLoading: boolean;
  roles: AppRole[];
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [roles, setRoles] = useState<AppRole[]>([]);

  const fetchUserRoles = async (userId: string) => {
    try {
      const { data, error } = await supabase.rpc('get_user_roles', { _user_id: userId });
      if (error) {
        console.error('Error fetching roles:', error);
        return [];
      }
      return (data as AppRole[]) || [];
    } catch (error) {
      console.error('Error fetching roles:', error);
      return [];
    }
  };

  useEffect(() => {
    let didInit = false;

    // Set up auth state listener FIRST — do NOT await anything here, it blocks the listener.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);

      if (session?.user) {
        setRolesLoading(true);
        fetchUserRoles(session.user.id).then((r) => {
          setRoles(r);
          setRolesLoading(false);
        });

        // Welcome email on fresh signup (non-blocking)
        if (event === 'SIGNED_IN' && session.user.created_at) {
          const createdAt = new Date(session.user.created_at).getTime();
          if (Date.now() - createdAt < 60_000) {
            const storedLang = (typeof window !== 'undefined' && (localStorage.getItem('language') || document.documentElement.lang)) || 'fr';
            const locale = storedLang === 'en' ? 'en' : 'fr';
            supabase.functions.invoke('send-transactional-email', {
              body: {
                templateName: 'welcome',
                recipientEmail: session.user.email,
                idempotencyKey: `welcome-${session.user.id}`,
                templateData: { name: session.user.user_metadata?.full_name || session.user.email, locale },
              },
            }).catch(() => {});
          }
        }
      } else {
        setRoles([]);
        setRolesLoading(false);
      }
    });

    // Then check for existing session (also non-blocking on roles)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (didInit) return;
      didInit = true;
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
      if (session?.user) {
        setRolesLoading(true);
        fetchUserRoles(session.user.id).then((r) => {
          setRoles(r);
          setRolesLoading(false);
        });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setRoles([]);
  };

  const isAdmin = roles.includes('admin');

  return (
    <AuthContext.Provider value={{ user, session, isLoading, rolesLoading, roles, isAdmin, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};